import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { useDataContext } from '@/shared/providers/DataProvider';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import DataCacheService from '@/shared/services/DataCacheService';
import AuthService, { privileges } from '@/shared/services/AuthService';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import { useQueryClient } from '@tanstack/react-query';
import { useGetCompanySettings } from '@/shared/hooks/settings/useGetCompanySettings';
import { useUpdateCompanySettings } from '@/shared/hooks/settings/useUpdateCompanySettings';
import { useGetHrmSettings } from '@/shared/hooks/settings/useGetHrmSettings';
import { useUpdateHrmSettings } from '@/shared/hooks/settings/useUpdateHrmSettings';
import { useUpdateComplianceSettings } from '@/shared/hooks/settings/useUpdateComplianceSettings';
import { useGetDocumentSettings } from '@/shared/hooks/settings/useGetDocumentSettings';
import { useUpdateDocumentSettings } from '@/shared/hooks/settings/useUpdateDocumentSettings';
import { useGetCompanyTemplates } from '@/shared/hooks/settings/useGetCompanyTemplates';
import { SettingsQueryKey } from '@/shared/hooks/settings/queryKey';
import useGetRegistrationResources from '@/shared/hooks/resource/useGetRegistrationResources';
import { useScrollToUrlAnchor } from '@/shared/hooks/useScrollToUrlAnchor';
import config from '@/config';
import SettingsCompanySelector from './components/SettingsCompanySelector';
import CompanyHeader from './components/CompanyHeader';
import BudgetSettingsSection from './components/BudgetSettingsSection';
import ComplianceSection from './components/ComplianceSection';
import HrmSettingsSection from './components/HrmSettingsSection';
import DocumentsSection from './components/DocumentsSection';
import PaymentMonitoringSection from './components/PaymentMonitoringSection';
import TaskManagementSection from './components/TaskManagementSection';
import InvoicePdfSection from './components/InvoicePdfSection';
import BillingEmailsSection from './components/BillingEmailsSection';
import BillingSetupSection from './components/BillingSetupSection';
import GeneralSection from './components/GeneralSection';
import SecuritySection from './components/SecuritySection';
import messages from './generalSettingsMessages';
const getHideLedgerSetting = () => {
    var _a;
    const createdOn = (_a = DataCacheService.getDataCache()) === null || _a === void 0 ? void 0 : _a.createdOn;
    if (!createdOn)
        return false;
    const releaseDate = moment('08-11-2021', 'DD-MM-YYYY');
    return moment.utc(createdOn).isAfter(releaseDate);
};
const GeneralSettingsContainer = () => {
    const intl = useIntl();
    const queryClient = useQueryClient();
    const { allCompanies, defaultCompany } = useDataContext();
    const [selectedCompanyId, setSelectedCompanyId] = useState(() => { var _a, _b, _c; return (_c = (_a = defaultCompany === null || defaultCompany === void 0 ? void 0 : defaultCompany.id) !== null && _a !== void 0 ? _a : (_b = allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : ''; });
    const selectedCompany = (allCompanies || []).find((c) => c.id === selectedCompanyId);
    const connectWebUrl = selectedCompanyId
        ? `${config.appUrls.connect}?companyId=${selectedCompanyId}`
        : config.appUrls.connect;
    const canReadSettings = AuthService.hasPermission({
        functionality: 'Settings',
        privilege: privileges.Read,
    });
    const canReadHrmSettings = canReadSettings ||
        AuthService.hasPermission({ functionality: 'Users', privilege: privileges.Update });
    const { data: settings, isLoading } = useGetCompanySettings(selectedCompanyId, canReadSettings);
    const { updateCompanySettings } = useUpdateCompanySettings();
    const { data: hrmSettings, isLoading: isHrmLoading } = useGetHrmSettings(selectedCompanyId, canReadHrmSettings);
    const { updateHrmSettings } = useUpdateHrmSettings();
    const { updateComplianceSettings } = useUpdateComplianceSettings();
    const { data: documentSettings, isLoading: isDocSettingsLoading } = useGetDocumentSettings(canReadSettings);
    const { updateDocumentSettings } = useUpdateDocumentSettings();
    const { data: templates } = useGetCompanyTemplates();
    const { data: resources } = useGetRegistrationResources(0);
    const handleCompanyChange = useCallback((company) => {
        setSelectedCompanyId(company.id);
    }, []);
    const handleFieldUpdate = useCallback(async (field, value) => {
        if (!settings)
            return;
        if (settings[field] === value)
            return;
        const model = Object.assign(Object.assign({}, settings), { [field]: value });
        try {
            await updateCompanySettings(model);
            ToastrService.success(intl.formatMessage(messages.msgChangesSavedForName, { name: selectedCompany === null || selectedCompany === void 0 ? void 0 : selectedCompany.name }));
        }
        catch (_a) {
            // error toastr already shown by useUpdateCompanySettings hook
        }
    }, [settings, updateCompanySettings, intl, selectedCompany]);
    const handleSettingsUpdate = useCallback(async (partialSettings) => {
        if (!settings || !partialSettings)
            return;
        const model = Object.assign(Object.assign({}, settings), partialSettings);
        try {
            await updateCompanySettings(model);
            ToastrService.success(intl.formatMessage(messages.msgChangesSavedForName, { name: selectedCompany === null || selectedCompany === void 0 ? void 0 : selectedCompany.name }));
        }
        catch (_a) {
            // error toastr already shown by useUpdateCompanySettings hook
        }
    }, [settings, updateCompanySettings, intl, selectedCompany]);
    const handleHrmFieldUpdate = useCallback(async (field, value) => {
        if (!hrmSettings)
            return;
        if (hrmSettings[field] === value)
            return;
        const model = Object.assign(Object.assign({}, hrmSettings), { [field]: value });
        try {
            await updateHrmSettings({ companyId: selectedCompanyId, model });
            ToastrService.changesSaved(intl);
        }
        catch (_a) {
            // error toastr already shown by useUpdateHrmSettings hook
        }
    }, [hrmSettings, selectedCompanyId, updateHrmSettings, intl]);
    const handleDocFieldUpdate = useCallback(async (field, value) => {
        if (!documentSettings)
            return;
        if (documentSettings[field] === value)
            return;
        const model = Object.assign(Object.assign({}, documentSettings), { [field]: value });
        try {
            await updateDocumentSettings(model);
            ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
        }
        catch (_a) {
            // error toastr already shown by useUpdateDocumentSettings hook
        }
    }, [documentSettings, updateDocumentSettings, intl]);
    const handleDocSettingsUpdate = useCallback(async (partialSettings) => {
        if (!documentSettings)
            return;
        const model = Object.assign(Object.assign({}, documentSettings), partialSettings);
        try {
            await updateDocumentSettings(model);
            ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
        }
        catch (_a) {
            // error toastr already shown by useUpdateDocumentSettings hook
        }
    }, [documentSettings, updateDocumentSettings, intl]);
    const handleComplianceUpdate = useCallback(async (setting) => {
        try {
            await updateComplianceSettings(setting);
            if (setting.shouldTrack) {
                ToastrService.success(intl.formatMessage(messages.msgComplianceRecalculationStarted));
            }
            else {
                ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
            }
        }
        catch (_a) {
            // error toastr already shown by useUpdateComplianceSettings hook
        }
    }, [updateComplianceSettings, intl]);
    const handleFieldUpdateWithConfirm = useCallback(async (field, value, propagate) => {
        if (!settings)
            return;
        const model = Object.assign(Object.assign({}, settings), { [field]: value, propagateLedgerChanges: propagate });
        try {
            await updateCompanySettings(model);
            ToastrService.success(intl.formatMessage(messages.msgChangesSavedForName, { name: selectedCompany === null || selectedCompany === void 0 ? void 0 : selectedCompany.name }));
        }
        catch (_a) {
            // error toastr already shown by useUpdateCompanySettings hook
        }
    }, [settings, updateCompanySettings, intl, selectedCompany]);
    const handleTeamsChanged = useCallback((teams, teamSecurityCode) => {
        if (!selectedCompanyId)
            return;
        const cachedCompany = DataCacheService.getCompanySettings(selectedCompanyId);
        if (cachedCompany) {
            cachedCompany.teamSecurityCode = teamSecurityCode;
        }
        queryClient.setQueryData([SettingsQueryKey.CompanySettings, selectedCompanyId], (old) => old ? Object.assign(Object.assign({}, old), { companyTeams: teams, teamSecurityCode }) : old);
        ToastrService.success(intl.formatMessage(messages.msgChangesSavedForName, { name: selectedCompany === null || selectedCompany === void 0 ? void 0 : selectedCompany.name }));
    }, [selectedCompanyId, selectedCompany, queryClient, intl]);
    const handleEmailDomainVerify = useCallback(async () => {
        if (!(settings === null || settings === void 0 ? void 0 : settings.companyId))
            return;
        try {
            const response = await api.base.get(`/api/settings/VerifyEmailDomain?id=${settings.companyId}`);
            const result = response.data;
            const model = Object.assign(Object.assign({}, settings), { emailUseCustomFrom: result.status === 1, emailDomain: result });
            await updateCompanySettings(model);
            if (result.status === 0) {
                ToastrService.error(intl.formatMessage(messages.msgEmailDomainVerificationFailed, {
                    domain: result.domain,
                }));
            }
            else if (result.status === 1) {
                ToastrService.success(intl.formatMessage(messages.msgEmailDomainVerificationSucceeded, {
                    domain: result.domain,
                }));
            }
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
    }, [settings, updateCompanySettings, intl]);
    const handleEmailDomainDelete = useCallback(async () => {
        if (!(settings === null || settings === void 0 ? void 0 : settings.companyId))
            return;
        try {
            await api.base.delete(`/api/settings/DeleteEmailDomain?id=${settings.companyId}`);
            const model = Object.assign(Object.assign({}, settings), { emailUseCustomFrom: false, emailDomain: undefined });
            await updateCompanySettings(model);
            ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
    }, [settings, updateCompanySettings, intl]);
    const handleEmailDomainAdded = useCallback(async (domain) => {
        if (!settings)
            return;
        const model = Object.assign(Object.assign({}, settings), { emailDomain: domain });
        await updateCompanySettings(model);
        ToastrService.success(intl.formatMessage(messages.msgEmailDomainAddedVerificationPending, {
            domain: domain.domain,
        }));
    }, [settings, updateCompanySettings, intl]);
    const handleEmailDomainVerifyResult = useCallback(async (result) => {
        if (!settings || !result)
            return;
        const model = Object.assign(Object.assign({}, settings), { emailUseCustomFrom: result.status === 1, emailDomain: result });
        await updateCompanySettings(model);
    }, [settings, updateCompanySettings]);
    const hideLedgerSetting = useMemo(() => getHideLedgerSetting(), []);
    const isPageLoading = (canReadSettings && isLoading) ||
        (canReadSettings && isDocSettingsLoading) ||
        (canReadHrmSettings && isHrmLoading);
    useScrollToUrlAnchor(!isPageLoading);
    useEffect(() => {
        const companyUpdatedHandler = (company) => {
            if (!settings || settings.companyId !== company.companyId)
                return;
            queryClient.invalidateQueries([SettingsQueryKey.CompanySettings, selectedCompanyId]);
        };
        SignalRService.subscribe('companyUpdated', companyUpdatedHandler);
        return () => SignalRService.unsubscribe('companyUpdated', companyUpdatedHandler);
    }, [settings, selectedCompanyId, queryClient]);
    return (_jsxs("div", Object.assign({ id: "CompanyPage" }, { children: [_jsx(SettingsCompanySelector, { companies: allCompanies || [], selectedCompanyId: selectedCompanyId, onCompanyChange: handleCompanyChange }), _jsx("div", Object.assign({ className: "panel" }, { children: _jsxs("div", Object.assign({ className: "panel-body" }, { children: [selectedCompany && _jsx(CompanyHeader, { company: selectedCompany }), _jsx("div", Object.assign({ className: "settings-page" }, { children: _jsxs(LoadingContainer, Object.assign({ isLoading: isPageLoading }, { children: [_jsx(GeneralSection, { settings: settings, documentSettings: documentSettings, onFieldUpdate: handleFieldUpdate, onDocFieldUpdate: handleDocFieldUpdate, onDocSettingsUpdate: handleDocSettingsUpdate, onEmailDomainVerify: handleEmailDomainVerify, onEmailDomainDelete: handleEmailDomainDelete, onEmailDomainAdded: handleEmailDomainAdded, onEmailDomainVerifyResult: handleEmailDomainVerifyResult, onTeamsChanged: handleTeamsChanged, selectedCompany: selectedCompany, connectWebUrl: connectWebUrl }), _jsx(SecuritySection, {}), _jsx(BillingSetupSection, { settings: settings, documentSettings: documentSettings, onFieldUpdate: handleFieldUpdate, onDocFieldUpdate: handleDocFieldUpdate, onFieldUpdateWithConfirm: handleFieldUpdateWithConfirm, onSettingsUpdate: handleSettingsUpdate, hideLedgerSetting: hideLedgerSetting, connectWebUrl: connectWebUrl }), _jsx(InvoicePdfSection, { settings: settings, onFieldUpdate: handleFieldUpdate, connectWebUrl: connectWebUrl }), _jsx(BillingEmailsSection, { settings: settings, templates: templates || [], onFieldUpdate: handleFieldUpdate, selectedCompany: selectedCompany }), _jsx(PaymentMonitoringSection, { settings: settings, templates: templates || [], onFieldUpdate: handleFieldUpdate, connectWebUrl: connectWebUrl }), _jsx(BudgetSettingsSection, { settings: settings, onFieldUpdate: handleFieldUpdate }), _jsx(TaskManagementSection, { settings: settings, resources: resources || [], onFieldUpdate: handleFieldUpdate }), _jsx(DocumentsSection, { settings: settings, documentSettings: documentSettings, onFieldUpdate: handleFieldUpdate, onDocFieldUpdate: handleDocFieldUpdate }), _jsx(ComplianceSection, { settings: settings, onComplianceUpdate: handleComplianceUpdate, onFieldUpdate: handleFieldUpdate }), _jsx(HrmSettingsSection, { hrmSettings: hrmSettings, onHrmFieldUpdate: handleHrmFieldUpdate, selectedCompanyId: selectedCompanyId })] })) }))] })) }))] })));
};
export default GeneralSettingsContainer;
